import { useEffect, useMemo, useState } from "react";
import client from "../api/client";

const today = () => new Date().toISOString().split("T")[0];
const companyId = () => Number(localStorage.getItem("companyId") || 0);

const val = (r, ...keys) => {
  for (const k of keys) {
    const x = r?.[k];
    if (x !== undefined && x !== null) return x;
  }
  return "";
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const money = (v) =>
  num(v).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const errText = (e, fb) => {
  const d = e?.response?.data;
  if (typeof d === "string") return d;
  if (d?.message) return d.message;
  if (d?.title) return d.title;
  return fb;
};

const styles = {
  page: { fontSize: 11, padding: 0 },
  card: {
    background: "#fff",
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
  },
  header: {
    background: "linear-gradient(90deg,#0b4f4a,#0f766e)",
    color: "#fff",
    padding: "8px 12px",
  },
  title: { fontSize: 13, fontWeight: 700, margin: 0 },
  sub: { fontSize: 10, opacity: 0.85 },
  body: { padding: 10 },
  label: { fontSize: 10, fontWeight: 700, marginBottom: 3 },
  input: { height: 30, fontSize: 11, borderRadius: 7 },
  btn: { height: 30, fontSize: 11, borderRadius: 7, fontWeight: 600 },
  tableWrap: {
    maxHeight: 360,
    overflow: "auto",
    border: "1px solid #e9ecef",
    borderRadius: 10,
  },
  table: { fontSize: 11, minWidth: 950, marginBottom: 0 },
  th: {
    fontSize: 10,
    color: "#fff",
    background: "#07111f",
    padding: "7px 8px",
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
  },
  td: { fontSize: 11, padding: "6px 8px", whiteSpace: "nowrap" },
};

export default function StockTransferPage() {
  const [warehouses, setWarehouses] = useState([]);
  const [items, setItems] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    transferNo: "",
    transferDate: today(),
    fromWarehouseId: "",
    toWarehouseId: "",
    notes: "",
  });

  const [detail, setDetail] = useState({
    itemId: "",
    qty: 1,
    rate: 0,
    remarks: "",
  });

  const [details, setDetails] = useState([]);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    const cid = companyId();

    try {
      const [wh, it, tr, no] = await Promise.allSettled([
        client.get("/inventory/warehouses", {
          params: cid > 0 ? { companyId: cid } : {},
        }),
        client.get("/inventory/items", {
          params: cid > 0 ? { companyId: cid } : {},
        }),
        client.get("/inventory/stock-transfers", {
          params: cid > 0 ? { companyId: cid } : {},
        }),
        client.get("/inventory/stock-transfers/next-no", {
          params: cid > 0 ? { companyId: cid } : {},
        }),
      ]);

      setWarehouses(
        wh.status === "fulfilled" && Array.isArray(wh.value.data)
          ? wh.value.data
          : [],
      );
      setItems(
        it.status === "fulfilled" && Array.isArray(it.value.data)
          ? it.value.data
          : [],
      );
      setTransfers(
        tr.status === "fulfilled" && Array.isArray(tr.value.data)
          ? tr.value.data
          : [],
      );

      if (no.status === "fulfilled") {
        setForm((f) => ({
          ...f,
          transferNo:
            val(no.value.data, "TransferNo", "transferNo") ||
            val(no.value.data, "transferNo") ||
            "",
        }));
      }
    } catch (e) {
      alert(errText(e, "Failed to load stock transfer data"));
    }
  };

  const selectedItem = useMemo(
    () =>
      items.find((x) => String(val(x, "Id", "id")) === String(detail.itemId)),
    [items, detail.itemId],
  );

  const addDetail = () => {
    if (!detail.itemId) return alert("Select item");
    if (num(detail.qty) <= 0)
      return alert("Quantity must be greater than zero");

    const itemName = val(selectedItem, "ItemName", "itemName");
    const itemCode = val(selectedItem, "ItemCode", "itemCode");

    setDetails([
      ...details,
      {
        itemId: Number(detail.itemId),
        itemCode,
        itemName,
        qty: num(detail.qty),
        rate: num(detail.rate),
        remarks: detail.remarks,
      },
    ]);

    setDetail({ itemId: "", qty: 1, rate: 0, remarks: "" });
  };

  const removeDetail = (index) => {
    setDetails(details.filter((_, i) => i !== index));
  };

  const save = async () => {
    if (!form.transferNo.trim()) return alert("Transfer No is required");
    if (!form.transferDate) return alert("Transfer Date is required");
    if (!form.fromWarehouseId) return alert("From Warehouse is required");
    if (!form.toWarehouseId) return alert("To Warehouse is required");
    if (form.fromWarehouseId === form.toWarehouseId)
      return alert("From and To warehouse cannot be same");
    if (details.length === 0) return alert("Add at least one item");

    try {
      setSaving(true);

      await client.post("/inventory/stock-transfers", {
        companyId: companyId(),
        transferNo: form.transferNo,
        transferDate: form.transferDate,
        fromWarehouseId: Number(form.fromWarehouseId),
        toWarehouseId: Number(form.toWarehouseId),
        notes: form.notes,
        details: details.map((x) => ({
          itemId: x.itemId,
          qty: x.qty,
          rate: x.rate,
          remarks: x.remarks,
        })),
      });

      alert("Stock transfer saved successfully");

      setForm({
        transferNo: "",
        transferDate: today(),
        fromWarehouseId: "",
        toWarehouseId: "",
        notes: "",
      });
      setDetails([]);
      await loadAll();
    } catch (e) {
      alert(errText(e, "Failed to save stock transfer"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-fluid py-2" style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h6 style={styles.title}>
            <i className="bi bi-arrow-left-right me-2"></i>
            Stock Transfer
          </h6>
          <div style={styles.sub}>
            Transfer stock from one warehouse to another warehouse
          </div>
        </div>

        <div style={styles.body}>
          <div className="row g-2 mb-2">
            <div className="col-md-2">
              <label style={styles.label}>Transfer No</label>
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.transferNo}
                onChange={(e) =>
                  setForm({ ...form, transferNo: e.target.value })
                }
              />
            </div>

            <div className="col-md-2">
              <label style={styles.label}>Transfer Date</label>
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={form.transferDate}
                onChange={(e) =>
                  setForm({ ...form, transferDate: e.target.value })
                }
              />
            </div>

            <div className="col-md-3">
              <label style={styles.label}>From Warehouse</label>
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.fromWarehouseId}
                onChange={(e) =>
                  setForm({ ...form, fromWarehouseId: e.target.value })
                }
              >
                <option value="">Select From Warehouse</option>
                {warehouses.map((w) => (
                  <option key={val(w, "Id", "id")} value={val(w, "Id", "id")}>
                    {val(w, "WarehouseCode", "warehouseCode")} -{" "}
                    {val(w, "WarehouseName", "warehouseName")}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label style={styles.label}>To Warehouse</label>
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.toWarehouseId}
                onChange={(e) =>
                  setForm({ ...form, toWarehouseId: e.target.value })
                }
              >
                <option value="">Select To Warehouse</option>
                {warehouses.map((w) => (
                  <option key={val(w, "Id", "id")} value={val(w, "Id", "id")}>
                    {val(w, "WarehouseCode", "warehouseCode")} -{" "}
                    {val(w, "WarehouseName", "warehouseName")}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-2">
              <label style={styles.label}>Notes</label>
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
          </div>

          <div
            className="row g-2 mb-2 p-2"
            style={{
              background: "#f8f9fa",
              borderRadius: 10,
              border: "1px solid #e9ecef",
            }}
          >
            <div className="col-md-4">
              <label style={styles.label}>Item</label>
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={detail.itemId}
                onChange={(e) => {
                  const item = items.find(
                    (x) =>
                      String(val(x, "Id", "id")) === String(e.target.value),
                  );
                  setDetail({
                    ...detail,
                    itemId: e.target.value,
                    rate: num(val(item, "CostPrice", "costPrice")),
                  });
                }}
              >
                <option value="">Select Item</option>
                {items.map((item) => (
                  <option
                    key={val(item, "Id", "id")}
                    value={val(item, "Id", "id")}
                  >
                    {val(item, "ItemCode", "itemCode")} -{" "}
                    {val(item, "ItemName", "itemName")}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-2">
              <label style={styles.label}>Qty</label>
              <input
                type="number"
                className="form-control form-control-sm"
                style={styles.input}
                value={detail.qty}
                onChange={(e) => setDetail({ ...detail, qty: e.target.value })}
              />
            </div>

            <div className="col-md-2">
              <label style={styles.label}>Rate</label>
              <input
                type="number"
                className="form-control form-control-sm"
                style={styles.input}
                value={detail.rate}
                onChange={(e) => setDetail({ ...detail, rate: e.target.value })}
              />
            </div>

            <div className="col-md-3">
              <label style={styles.label}>Remarks</label>
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={detail.remarks}
                onChange={(e) =>
                  setDetail({ ...detail, remarks: e.target.value })
                }
              />
            </div>

            <div className="col-md-1 d-flex align-items-end">
              <button
                className="btn btn-primary btn-sm w-100"
                style={styles.btn}
                onClick={addDetail}
              >
                Add
              </button>
            </div>
          </div>

          <div style={styles.tableWrap} className="mb-2">
            <table
              className="table table-bordered table-hover align-middle"
              style={styles.table}
            >
              <thead>
                <tr>
                  {[
                    "Item Code",
                    "Item Name",
                    "Qty",
                    "Rate",
                    "Amount",
                    "Remarks",
                    "Action",
                  ].map((h) => (
                    <th
                      key={h}
                      style={styles.th}
                      className={
                        ["Qty", "Rate", "Amount"].includes(h) ? "text-end" : ""
                      }
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {details.length === 0 ? (
                  <tr>
                    <td
                      colSpan="7"
                      className="text-center text-muted"
                      style={{ ...styles.td, padding: 18 }}
                    >
                      No items added
                    </td>
                  </tr>
                ) : (
                  details.map((d, i) => (
                    <tr key={i}>
                      <td style={styles.td}>{d.itemCode}</td>
                      <td style={styles.td}>{d.itemName}</td>
                      <td className="text-end" style={styles.td}>
                        {money(d.qty)}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {money(d.rate)}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {money(d.qty * d.rate)}
                      </td>
                      <td style={styles.td}>{d.remarks || "-"}</td>
                      <td style={styles.td}>
                        <button
                          className="btn btn-outline-danger btn-sm"
                          style={{ fontSize: 10 }}
                          onClick={() => removeDetail(i)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-end mb-3">
            <button
              className="btn btn-success btn-sm"
              style={{ ...styles.btn, minWidth: 140 }}
              onClick={save}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Transfer"}
            </button>
          </div>

          <div style={styles.tableWrap}>
            <table
              className="table table-bordered table-hover align-middle"
              style={styles.table}
            >
              <thead>
                <tr>
                  {[
                    "Date",
                    "Transfer No",
                    "From Warehouse",
                    "To Warehouse",
                    "Notes",
                  ].map((h) => (
                    <th key={h} style={styles.th}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {transfers.length === 0 ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="text-center text-muted"
                      style={{ ...styles.td, padding: 18 }}
                    >
                      No transfers found
                    </td>
                  </tr>
                ) : (
                  transfers.map((t, i) => (
                    <tr key={i}>
                      <td style={styles.td}>
                        {String(val(t, "TransferDate", "transferDate")).slice(
                          0,
                          10,
                        )}
                      </td>
                      <td style={{ ...styles.td, fontWeight: 700 }}>
                        {val(t, "TransferNo", "transferNo")}
                      </td>
                      <td style={styles.td}>
                        {val(t, "FromWarehouse", "fromWarehouse")}
                      </td>
                      <td style={styles.td}>
                        {val(t, "ToWarehouse", "toWarehouse")}
                      </td>
                      <td style={styles.td}>
                        {val(t, "Notes", "notes") || "-"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
